import dayjs, { type Dayjs } from 'dayjs';

export type YearsRevenueMode = 'FISCAL' | 'CALENDAR';

export interface YearRange {
  startDate: string;
  endDate: string;
}

export interface IndianFinancialYearRange extends YearRange {}

/**
 * Indian financial year (1 Apr – 31 Mar) that contains `reference`.
 * Jan–Mar belong to the FY that started the previous calendar April.
 */
export function getIndianFinancialYearRange(reference: Dayjs = dayjs()): IndianFinancialYearRange {
  const y = reference.year();
  const m = reference.month();
  const startYear = m < 3 ? y - 1 : y;
  const start = dayjs(`${startYear}-04-01`);
  const end = dayjs(`${startYear + 1}-03-31`);
  return {
    startDate: start.format('YYYY-MM-DD'),
    endDate: end.format('YYYY-MM-DD'),
  };
}

/** Calendar year (1 Jan – 31 Dec) that contains `reference`. */
export function getCalendarYearRange(reference: Dayjs = dayjs()): YearRange {
  const y = reference.year();
  return {
    startDate: dayjs(`${y}-01-01`).format('YYYY-MM-DD'),
    endDate: dayjs(`${y}-12-31`).format('YYYY-MM-DD'),
  };
}

export function getYearRangeForMode(
  mode: YearsRevenueMode,
  reference: Dayjs = dayjs(),
): YearRange {
  return mode === 'CALENDAR' ? getCalendarYearRange(reference) : getIndianFinancialYearRange(reference);
}

export function getCurrentIndianFinancialYearRange(): IndianFinancialYearRange {
  return getIndianFinancialYearRange(dayjs());
}

/** Subtitle for FY-to-date metrics, e.g. {@code 1 Apr – 19 May 2026}. */
export function formatFinancialYearToDateSubtitle(reference: Dayjs = dayjs()): string {
  const { startDate } = getIndianFinancialYearRange(reference);
  return `${dayjs(startDate).format('D MMM')} – ${reference.format('D MMM YYYY')}`;
}

/** Subtitle for full Indian FY bounds, e.g. {@code (1 Apr to 31 Mar 2027)}. */
export function formatFinancialYearSubtitle(reference: Dayjs = dayjs()): string {
  return formatYearRangeSubtitle('FISCAL', reference);
}

/** Subtitle for Years Revenue, e.g. {@code (1 Apr to 31 Mar 2027)} or {@code (1 Jan to 31 Dec 2027)}. */
export function formatYearRangeSubtitle(
  mode: YearsRevenueMode = 'FISCAL',
  reference: Dayjs = dayjs(),
): string {
  const { startDate, endDate } = getYearRangeForMode(mode, reference);
  return `(${dayjs(startDate).format('D MMM')} to ${dayjs(endDate).format('D MMM YYYY')})`;
}
